import { useEffect, useRef, useState } from 'preact/hooks';
import { t } from '../i18n/index.js';
import { ConfirmDialog } from '../components/ConfirmDialog.js';
import { GameIcon } from '../components/GameIcon.js';
import { ChatPanel } from '../components/ChatPanel.js';
import { RulesDialog } from '../components/RulesDialog.js';
import { formatDuration, useServerNow } from '../arena/useArena.js';
import { useTurnTicks } from '../games/common/useTurnTicks.js';
import { playSound } from '../audio/sounds.js';
import { DiceGame } from '../games/dice/DiceGame.js';
import { ValeGame } from '../games/vale/ValeGame.js';
import { turnClock } from '../../shared/arena/turnClock.js';
import type { Realtime } from '../realtime.js';
import type { ArenaResult, MultiGameStateView, MultiGameType, Role } from '../../shared/protocol.js';
import type { DiceView } from '../../shared/games/dice/view.js';
import type { ValeView } from '../../shared/games/vale/view.js';

/** Keeps the multiplayer game state in sync: pushed by the server, re-requested after reconnecting. */
function useMultiState(realtime: Realtime, gameId: number): MultiGameStateView | null {
  const [state, setState] = useState<MultiGameStateView | null>(null);
  const socket = realtime.socket;
  useEffect(() => {
    if (!socket) return;
    const onState = (s: MultiGameStateView) => setState(s);
    const sync = () =>
      socket.emit('multi:sync', (r: ArenaResult<{ state: MultiGameStateView | null }>) => {
        if (r.ok) setState(r.state);
      });
    socket.on('multi:state', onState);
    socket.on('connect', sync);
    if (socket.connected) sync();
    return () => {
      socket.off('multi:state', onState);
      socket.off('connect', sync);
    };
  }, [socket, gameId]);
  return state && state.id === gameId ? state : null;
}

/** Whose turn, the turn timer as a bar and the time bank as a countdown (like two-player games). */
function MultiTurnStatus(props: { state: MultiGameStateView; myId: number; serverNow: number }) {
  const { state, serverNow } = props;
  const current = state.players.find((p) => p.id === state.turn?.userId) ?? null;
  const myTurn = current?.id === props.myId;
  const clock =
    state.turn && current
      ? turnClock({ turnStartedAt: state.turn.startedAt, turnTimeMs: state.settings.turnTimeMs, timeBankMs: current.timeBankMs, now: serverNow })
      : null;
  const fraction = clock ? Math.max(0, Math.min(1, clock.turnRemainingMs / state.settings.turnTimeMs)) : 0;

  const first = useRef(true);
  useEffect(() => {
    if (!first.current && myTurn) playSound('turn', 300);
    first.current = false;
  }, [state.turn?.userId, state.turn?.startedAt]);
  useTurnTicks(clock, myTurn);

  return (
    <div class={`turn-banner ${myTurn ? 'mine' : 'theirs'}`} role="status" key={state.turn?.startedAt}>
      <div class="turn-text">{myTurn ? t.turn.yours : current ? t.multi.turnOf(current.displayName) : ''}</div>
      {clock && clock.phase === 'turn' && (
        <div class="turn-bar" aria-hidden="true">
          <div class="turn-bar-fill" style={{ width: `${fraction * 100}%` }} />
        </div>
      )}
      {clock && clock.phase !== 'turn' && (
        <div class="time-bank">
          {t.turn.timeBank}: <strong>{formatDuration(clock.bankRemainingMs)}</strong>
        </div>
      )}
    </div>
  );
}

/** Multiplayer game screen (decision 2026-09-27): common frame + the game module's own UI. */
export function MultiGameView(props: { game: { id: number; gameType: MultiGameType }; realtime: Realtime; myId: number; role: Role }) {
  const { game, realtime } = props;
  const state = useMultiState(realtime, game.id);
  const serverNow = useServerNow(realtime.clockOffset, 200);
  const [confirming, setConfirming] = useState(false);
  const [showRules, setShowRules] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const otherHumans = state ? state.players.some((p) => p.id !== props.myId && !p.isAi && !p.dropped) : false;

  const leave = () => {
    setConfirming(false);
    realtime.socket?.emit('multi:resign', (r: ArenaResult) => {
      if (!r.ok) setError(t.arena.errors[r.error]);
    });
  };

  return (
    <section class="game-view multi-game">
      <div class="game-head">
        <h2>
          <GameIcon game={game.gameType} size={30} /> {t.games[game.gameType].name}
        </h2>
        {realtime.socket && otherHumans && <ChatPanel socket={realtime.socket} gameId={game.id} myId={props.myId} role={props.role} />}
        <button class="btn btn-small" onClick={() => setShowRules(true)}>
          📖 {t.rules.buttonShort}
        </button>
        <button class="btn btn-danger btn-small" onClick={() => setConfirming(true)}>
          {t.multi.leave}
        </button>
      </div>
      {error && <p class="error">{error}</p>}

      {!state || !realtime.socket ? (
        <div class="card muted">{t.status.connecting}</div>
      ) : (
        <>
          <MultiTurnStatus state={state} myId={props.myId} serverNow={serverNow} />
          {state.gameType === 'vale' ? (
            <ValeGame state={state as MultiGameStateView<ValeView>} socket={realtime.socket} myId={props.myId} serverNow={serverNow} />
          ) : (
            <DiceGame state={state as MultiGameStateView<DiceView>} socket={realtime.socket} myId={props.myId} />
          )}
          <p class="muted small-note">{t.multi.timeoutNote}</p>
        </>
      )}

      {showRules && <RulesDialog game={game.gameType} settings={state?.settings} onClose={() => setShowRules(false)} />}
      {confirming && (
        <ConfirmDialog
          title={t.multi.leaveTitle}
          confirmLabel={t.multi.leave}
          cancelLabel={t.game.cancel}
          danger
          onConfirm={leave}
          onCancel={() => setConfirming(false)}
        >
          <p>{t.multi.leaveText}</p>
        </ConfirmDialog>
      )}
    </section>
  );
}
